import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { MessageNotificationService } from 'message-notification-service-lib';
import { EMPTY, forkJoin, Observable, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Adv2ConfigService } from '../../../services/adv2-config.service';
import { ServiceProviderServiceFactory } from '../../../services/service-providers/service-provider-service.factory';
import {
  IDocusignBrand,
  IDocusignTemplate,
  IDocusignUserAccount,
} from '../../../services/signature-providers/docusign/docusign-config.service';

export enum SettingType {
  None = 'none',
  Default = 'default',
  Custom = 'custom',
}

interface IBaseConfig<T> {
  list: T[];
  loading: boolean;
  isValid: boolean;
  toggleValue: SettingType; // Data bound to the toggle button in the UI
}

interface IUserAccountConfig extends IBaseConfig<IDocusignUserAccount> {
  defaultAccount: { accountId: string; accountName: string };
}

interface IBrandsConfig extends IBaseConfig<IDocusignBrand> {
  accountBrandsMap: { [accountId: string]: IDocusignBrand[] };
}

interface ITemplateConfig extends IBaseConfig<IDocusignTemplate> {
  accountTemplatesMap: { [accountId: string]: IDocusignTemplate[] };
  currentTemplateRoles: string[];
}

export interface IDocusignConfig {
  loadingSuccess: boolean;
  accounts: IUserAccountConfig;
  brands: IBrandsConfig;
  templates: ITemplateConfig;
}

interface IDocusignConfigControls {
  account: FormControl;
  brand: FormControl;
  template: FormControl;
  documentPosition: FormControl;
}

@Injectable({
  providedIn: 'root',
})
export class DocusignTabService {
  constructor(
    private adv2ConfigService: Adv2ConfigService,
    private messageNotificationService: MessageNotificationService,
    private serviceProviderService: ServiceProviderServiceFactory
  ) {}

  //#region Public methods
  loadUserConfig(config: IDocusignConfig, formGroup: FormGroup, controls: IDocusignConfigControls): void {
    this.adv2ConfigService
      .getConfig()
      .pipe(
        switchMap((adv2Config) => {
          if (!adv2Config?.config?.signatureId) {
            // Prevents NPE on the backend when making a call to fetch Docusign config which doesn't exist (KAD2-2625)
            this.setLoadingFailed(config);
            return EMPTY;
          }
          return this.loadUserAccounts(config, adv2Config, controls);
        }),
        switchMap(() =>
          forkJoin({
            brands: this.getUserAccountsData(config, 'brands'),
            templates: this.getUserAccountsData(config, 'templates'),
          })
        ),
        tap(({ brands }) => this.handleUserBrands(config, brands, controls)),
        tap(({ templates }) => this.handleUserTemplates(config, templates, formGroup, controls)),
        tap(() => this.addRecipientsRolesValidator(config, formGroup)),
        tap(() => controls.account.enable({ emitEvent: false, onlySelf: true }))
      )
      .subscribe({
        error: (err) => {
          this.setLoadingFailed(config);
          this.messageNotificationService.error('Error loading user configuration.', err);
        },
      });
  }

  getSelectedAccountName(config: IDocusignConfig, control: FormControl) {
    if (config.accounts.toggleValue === SettingType.Default) {
      return config.accounts.defaultAccount?.accountName;
    }
    return config.accounts.list?.find((account) => account.account_id === control.value)?.account_name;
  }

  getSelectedBrandName(config: IDocusignConfig, control: FormControl) {
    if (config.brands.toggleValue === SettingType.Default) {
      return config.brands.list?.find((brand) => brand.isSigningDefault)?.brandName;
    }
    return config.brands.list?.find((brand) => brand.brandId === control.value)?.brandName;
  }

  getSelectedTemplateName(config: IDocusignConfig, control: FormControl) {
    if (config.templates.toggleValue === SettingType.None) {
      return null;
    }
    return config.templates.list?.find((template) => template.templateId === control.value)?.name;
  }

  getTemplateRecipientsRoles(config: IDocusignConfig, templateId: string): string[] {
    const template = config.templates.list.find((template) => template.templateId === templateId);
    if (!template) {
      return [];
    }
    const roles = [];
    if (template.recipients?.signers) {
      roles.push(...template.recipients.signers.map((recipient) => recipient.roleName));
    }
    if (template.recipients?.carbonCopies) {
      roles.push(...template.recipients.carbonCopies.map((recipient) => recipient.roleName));
    }
    if (template.recipients?.editors) {
      roles.push(...template.recipients.editors.map((recipient) => recipient.roleName));
    }
    return [...new Set(roles)];
  }

  addRecipientsRolesValidator(config: IDocusignConfig, formGroup: FormGroup) {
    const recipientsFormArray = formGroup.controls.recipients as FormArray;
    recipientsFormArray.controls.forEach((recipientFormGroup: FormGroup) => {
      recipientFormGroup.controls.role.addValidators((control) => {
        if (!control.value && config.templates.currentTemplateRoles.length) {
          return { required: true };
        }
        return null;
      });
      recipientFormGroup.controls.role.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  clearRecipientRolesValues(formGroup: FormGroup, markFormDirty = true) {
    const recipientsFormArray = formGroup.controls.recipients as FormArray;
    recipientsFormArray.controls.forEach((recipientFormGroup: FormGroup) => {
      recipientFormGroup.controls.role.setValue(null);
      if (markFormDirty) {
        recipientFormGroup.controls.role.markAsDirty();
      }
    });
  }

  setValidators(pairs: [AbstractControl, any][]): void {
    pairs.forEach((pair) => {
      const control = pair[0];
      const validators = pair[1];
      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }

  clearValidators(controls: any[]): void {
    controls.forEach((control) => {
      control.clearValidators();
      control.updateValueAndValidity();
    });
  }

  clearControls(controls: any[]): void {
    controls.forEach((control) => {
      control.setValue(null);
      control.markAsDirty();
    });
  }

  //#endregion

  //#region Private methods
  private setLoadingFailed(config: IDocusignConfig): void {
    // prevents the spinners from staying forever
    config.loadingSuccess = false;
    config.accounts.loading = false;
    config.brands.loading = false;
    config.templates.loading = false;
  }

  private loadUserAccounts(config: IDocusignConfig, adv2Config: any, controls: IDocusignConfigControls): Observable<any> {
    return this.serviceProviderService
      .getSignatureProviderService('docusign')
      .getSignatureConfigService()
      .getUserAccounts()
      .pipe(tap((response: IDocusignUserAccount[]) => this.handleUserAccounts(config, adv2Config, response, controls)));
  }

  private handleUserAccounts(
    config: IDocusignConfig,
    adv2Config: any,
    response: IDocusignUserAccount[],
    controls: IDocusignConfigControls
  ) {
    if (!response || !response.length) {
      config.accounts.loading = false;
      config.loadingSuccess = false;
      return throwError('No user accounts found');
    }

    config.accounts.list = response;
    config.accounts.defaultAccount = {
      accountId: adv2Config.signature.accountId,
      accountName: config.accounts.list.find((account) => account.account_id === adv2Config.signature.accountId)?.account_name,
    };
    this.loadAccountSelectionSettings(config, controls.account);
    config.accounts.loading = false;
  }

  private handleUserBrands(config: IDocusignConfig, brands: any, controls: IDocusignConfigControls) {
    config.brands.accountBrandsMap = brands;
    config.brands.list = config.brands.accountBrandsMap[controls.account.value || config.accounts.defaultAccount.accountId] ?? [];
    this.loadBrandSelectionSettings(config, controls.brand);
    config.brands.loading = false;
  }

  private handleUserTemplates(config: IDocusignConfig, templates: any, formGroup: FormGroup, controls: IDocusignConfigControls) {
    config.templates.accountTemplatesMap = templates;
    config.templates.list = config.templates.accountTemplatesMap[controls.account.value || config.accounts.defaultAccount.accountId] ?? [];
    config.templates.currentTemplateRoles = this.getTemplateRecipientsRoles(config, controls.template.value);
    this.loadTemplateSelectionSettings(config, formGroup, controls);
    config.templates.loading = false;
  }

  private getUserAccountsData(config: IDocusignConfig, dataType: 'brands' | 'templates'): Observable<any> {
    const docusignConfigService = this.serviceProviderService.getSignatureProviderService('docusign').getSignatureConfigService();

    // Determine the appropriate service method based on dataType
    const operations = {};
    config.accounts.list.forEach((account) => {
      if (dataType === 'brands') {
        operations[account.account_id] = docusignConfigService.getUserBrands(account.account_id);
      } else if (dataType === 'templates') {
        operations[account.account_id] = docusignConfigService.getUserTemplates(account.account_id);
      }
    });

    return forkJoin(operations);
  }

  private loadAccountSelectionSettings(config: IDocusignConfig, accountControl: FormControl): void {
    if (accountControl.value) {
      // Set toggle value to custom if account is selected
      config.accounts.toggleValue = SettingType.Custom;

      // Check if selected account is in the list of accounts
      const selectedAccount = config.accounts.list.find((account) => account.account_id === accountControl.value);
      if (!selectedAccount) {
        // Invalid Entry case
        config.accounts.isValid = false; // Account is invalid (not in list of accounts)
        accountControl.setValue(null);
        accountControl.markAsDirty();
      }
    } else {
      config.accounts.toggleValue = SettingType.Default;
    }
  }

  private loadBrandSelectionSettings(config: IDocusignConfig, brandControl: FormControl): void {
    const brandId = brandControl.value;

    if (brandId) {
      // Set toggle value to custom if brand is selected
      config.brands.toggleValue = SettingType.Custom;

      // Check if selected brand is in the list of brands
      const selectedBrand = config.brands.list.find((brand) => brand.brandId === brandId);
      if (!selectedBrand) {
        // Invalid Entry case
        config.brands.isValid = false; // Brand is invalid (not in list of brands)
        brandControl.setValue(null);
        brandControl.markAsDirty();
      }
    } else {
      config.brands.toggleValue = SettingType.Default;
    }
  }

  private loadTemplateSelectionSettings(config: IDocusignConfig, formGroup: FormGroup, controls: IDocusignConfigControls): void {
    const templateId = controls.template.value;

    if (templateId) {
      // Set toggle value to custom
      config.templates.toggleValue = SettingType.Custom;

      // Check if selected template is in the list of templates
      const selectedTemplate = config.templates.list.find((template) => template.templateId === templateId);
      if (!selectedTemplate) {
        // Invalid Entry case
        config.templates.isValid = false; // Template is invalid (not in list of templates)
        this.clearControls([controls.template, controls.documentPosition]);
      }
    } else {
      config.templates.toggleValue = SettingType.None;
      this.clearRecipientRolesValues(formGroup, false);
    }
  }

  //#endregion
}
